import React from "react";
import classnames from "classnames";
import { Button } from "@blueprintjs/core";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";

/**
 * Кнопка-значение поля выбора: выглядит как закрытый `SimpleSelect`
 * (класс `selected-option`, во всю ширину). Нужна в двух случаях:
 *
 * - поле только для чтения — значение показывается так же, как в селекте,
 *   но выбрать ничего нельзя;
 * - своя кнопка внутри `SimpleSelect` / `Select`, когда `DefaultSelectedOption`
 *   не подходит (другой текст, своя иконка).
 *
 * ```tsx
 * {readonly ? <SelectButton text={stage.label} /> : <SimpleSelect …><DefaultSelectedOption … /></SimpleSelect>}
 * <SelectButton text={label} chevron disabled={!canChange} />
 * ```
 *
 * Для обычного выбора из списка по-прежнему `DefaultSelectedOption`: он умеет
 * крестик очистки и «нет данных».
 *
 * Вид селекта кнопка получает **только внутри** `SimpleSelect` / `Select`: стили
 * `selected-option` заданы в теме для `.select.bp6-popover-target` (Blueprint
 * навешивает этот класс на обёртку селекта). Сама по себе она выглядит как
 * обычная белая кнопка. Так она и используется во всём приложении — ребёнком
 * селекта или в рендерере, который селекту передают.
 */
export const SelectButton = ({ text, chevron, rightIcon, className, fill = true, ...props }: SelectButtonProps) => (
    <Button
        {...props}
        fill={fill}
        className={classnames("selected-option", className)}
        text={text}
        rightIcon={chevron ? <GeneralIcon type="general-chevron-down" /> : rightIcon}
    />
);

export interface SelectButtonProps extends Omit<React.ComponentProps<typeof Button>, "rightIcon"> {
    /** Шеврон справа, как у раскрывающегося селекта. */
    chevron?: boolean;
    /** Своя иконка справа; при `chevron` не используется. */
    rightIcon?: React.ComponentProps<typeof Button>["rightIcon"];
}
